import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Calendar, Bug, Zap, Shield, Sparkles } from 'lucide-react';

interface ReleaseNote {
  version: string;
  date: string;
  type: 'feature' | 'bugfix' | 'security' | 'performance' | 'enhancement';
  title: string;
  description: string;
}

interface ReleaseVersion {
  version: string;
  date: string;
  notes: ReleaseNote[];
}

const ReleaseNotes: React.FC = () => {
  const [releases, setReleases] = useState<ReleaseVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadReleaseNotes();
  }, []);

  const loadReleaseNotes = async () => {
    try {
      setLoading(true);
      
      // Try to load the latest release notes from public/release-notes
      const response = await fetch('/release-notes/latest.json');
      
      if (response.ok) {
        const data = await response.json();
        setReleases(Array.isArray(data) ? data : [data]);
      } else {
        // Fallback to embedded content if files don't exist yet
        setReleases(getEmbeddedReleaseNotes());
      }
    } catch (err) {
      console.warn('Could not load release notes from files, using embedded content:', err);
      setReleases(getEmbeddedReleaseNotes());
    } finally {
      setLoading(false);
    }
  };

  const getEmbeddedReleaseNotes = (): ReleaseVersion[] => {
    return [
      {
        version: "2.1.0",
        date: "2025-01-08",
        notes: [
          {
            version: "2.1.0",
            date: "2025-01-08",
            type: "enhancement",
            title: "Routes.js Refactoring Complete",
            description: "Successfully refactored the massive routes.js file (5,510 lines) into a clean, maintainable service layer architecture. Separated routing concerns from business logic, improving testability and maintainability."
          },
          {
            version: "2.1.0",
            date: "2025-01-08",
            type: "feature",
            title: "Service Layer Architecture",
            description: "Implemented comprehensive service layer with 12 specialized services: AuthService, TokenService, UserService, TransactionService, PaymentMethodService, MerchantService, ContactService, ReferralService, AdminService, NotificationService, ConfigService, and AnalyticsService."
          },
          {
            version: "2.1.0",
            date: "2025-01-08",
            type: "bugfix",
            title: "Platform Startup Crash Fixed",
            description: "Resolved critical startup crash caused by missing service imports. Routes.js now works with existing codebase while maintaining the foundation for gradual service layer adoption."
          },
          {
            version: "2.1.0",
            date: "2025-01-08",
            type: "enhancement",
            title: "Release Notes Component Fixed",
            description: "Fixed syntax errors in ReleaseNotes component and improved the interface with proper TypeScript types, error handling, and loading states."
          }
        ]
      }
    ];
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <Sparkles className="w-4 h-4 text-blue-500" />;
      case 'bugfix':
        return <Bug className="w-4 h-4 text-red-500" />;
      case 'security':
        return <Shield className="w-4 h-4 text-yellow-500" />;
      case 'performance':
        return <Zap className="w-4 h-4 text-green-500" />;
      case 'enhancement':
        return <Sparkles className="w-4 h-4 text-purple-500" />;
      default:
        return <Calendar className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feature':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'bugfix':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'security':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'performance':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'enhancement':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const toggleVersion = (version: string) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(version)) {
      newExpanded.delete(version);
    } else {
      newExpanded.add(version);
    }
    setExpandedVersions(newExpanded);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-4">
                <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Release Notes</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Release Notes</h1>
        <p className="text-gray-600">Stay updated with the latest features, improvements, and bug fixes.</p>
      </div>

      <div className="space-y-6">
        {releases.map((release) => (
          <div key={release.version} className="border border-gray-200 rounded-lg overflow-hidden">
            <div 
              className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => toggleVersion(release.version)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Version {release.version}
                  </h2>
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(release.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">
                    {release.notes.length} {release.notes.length === 1 ? 'update' : 'updates'}
                  </span>
                  {expandedVersions.has(release.version) ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {expandedVersions.has(release.version) && (
              <div className="p-4 bg-white">
                <div className="space-y-4">
                  {release.notes.map((note, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 mt-0.5">
                        {getTypeIcon(note.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-sm font-medium text-gray-900">{note.title}</h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getTypeColor(note.type)}`}>
                            {note.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{note.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {releases.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Release Notes Available</h3>
          <p className="text-gray-500">Release notes will appear here when available.</p>
        </div>
      )}
    </div>
  );
};

export default ReleaseNotes; 